import Figure from "@hydrologiq/shared/figures/generic/Figure"
import { roundToDP } from "@hydrologiq/common/helpers/math"
import FigureGrid from "@hydrologiq/shared/figures/generic/FigureGrid"
import TimeToDepletion from "@hydrologiq/shared/figures/generic/TimeToDepletion"
import RefuelNeededIn from "@hydrologiq/shared/figures/generic/RefuelNeededIn"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"

interface RentalHeadlinesProps {
  loading?: boolean
  error?: string | null
  assetsOnHire: number
  totalAlerts: number
  totalCo2eYesterday: number
  earliestRunDryMinutes?: number
  earliestRunDrySite?: { id: string; name: string }
  earliestRunDryCustomer?: { id: string; name: string }
  // Installation-level run_dry data (for fuel storage systems)
  earliestInstallationRunDryMinutes?: number
  earliestInstallationRunDrySite?: { id: string; name: string }
  earliestInstallationRunDrySystem?: { id: string; name: string }
}

function RentalHeadlines({
  loading,
  error,
  assetsOnHire,
  totalAlerts,
  totalCo2eYesterday,
  earliestRunDryMinutes,
  earliestRunDrySite,
  earliestRunDryCustomer,
  earliestInstallationRunDryMinutes,
  earliestInstallationRunDrySite,
  earliestInstallationRunDrySystem,
}: RentalHeadlinesProps) {
  const assetDepletionEnabled = useGetFeature("asset_depletion")
  // Extract rental-specific metrics from unified response
  const rounded = {
    assetsOnHire: assetsOnHire ?? 0,
    totalAlerts: totalAlerts ?? 0,
    totalCo2eYesterday: roundToDP(totalCo2eYesterday ?? 0, 2),
  }

  return (
    <FigureGrid loading={loading}>
      {/* Assets on Hire */}
      <Figure heading="Assets on Hire" error={error ?? undefined} loading={loading}>
        <FigureContent.Root>
          <FigureContent.SubText>current</FigureContent.SubText>
          <FigureContent.Value>{rounded.assetsOnHire}</FigureContent.Value>
        </FigureContent.Root>
      </Figure>

      {/* Refuel needed in (Installation-level) */}
      <RefuelNeededIn
        heading="Refuel needed in"
        minutes={earliestInstallationRunDryMinutes} // Use installation data with system linking
        location={earliestInstallationRunDrySite?.name}
        system={earliestInstallationRunDrySystem?.name}
        systemId={earliestInstallationRunDrySystem?.id}
        customer={earliestRunDryCustomer?.id}
        site={earliestInstallationRunDrySite?.id}
        granularity="days"
        loading={loading}
        error={error ?? undefined}
        isPlaceholder={!earliestInstallationRunDryMinutes}
      />

      {/* Asset Depleted in (Asset-level) */}
      {assetDepletionEnabled && (
        <TimeToDepletion
          heading="Asset Depleted in"
          minutes={earliestRunDryMinutes}
          location={earliestRunDrySite?.name}
          customer={earliestRunDryCustomer?.id}
          site={earliestRunDrySite?.id}
          granularity="days"
          loading={loading}
          error={error ?? undefined}
          isPlaceholder={!earliestRunDryMinutes}
        />
      )}

      {/* Number of Alerts */}
      <Figure heading="Alerts" error={error ?? undefined} loading={loading}>
        <FigureContent.Root>
          <FigureContent.SubText link={<FigureContent.Label>{rounded.totalAlerts === 0 ? "No active alerts" : "Requires attention"}</FigureContent.Label>}>current</FigureContent.SubText>
          <FigureContent.Value color={rounded.totalAlerts > 0 ? "red.500" : undefined}>{rounded.totalAlerts}</FigureContent.Value>
        </FigureContent.Root>
      </Figure>

      {/* Total CO2e Yesterday */}
      <Figure heading="Total emissions" error={error ?? undefined} loading={loading}>
        <FigureContent.Root>
          <FigureContent.SubText>yesterday</FigureContent.SubText>
          <FigureContent.Value>{rounded.totalCo2eYesterday} kg</FigureContent.Value>
        </FigureContent.Root>
      </Figure>
    </FigureGrid>
  )
}

export default RentalHeadlines
